// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, Droplets, Zap, Plus, User, Gauge, Pencil, CreditCard, CalendarPlus } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, Modal, Input, Select, EmptyState, SetupGuide, Skeleton, ConfirmDialog } from '@/components/ui'
import { useUnit, useDeleteUnit } from '@/hooks/useUnits'
import { useRoomType } from '@/hooks/useRoomTypes'
import { useProperty } from '@/hooks/useProperties'
import { useMeters, useCreateMeter, useUpdateMeter, useDeleteMeter } from '@/hooks/useMeters'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import type { Meter } from '@/types'
import { TransferOwnershipModal } from '@/pages/shared-owners/components/TransferOwnershipModal'
import { OwnershipHistory } from '@/pages/shared-owners/components/OwnershipHistory'
import { UnitBookingCard } from '@/components/units/UnitBookingCard'
import { BookingModal } from '@/components/units/BookingModal'
import { useSetupScopeSession } from '@/hooks/useSupportSession'

interface MeterFormData {
  meter_type: 'water' | 'electricity'
  serial_number: string
}

interface EditMeterFormData {
  serial_number: string
}

export function UnitDetailPage() {
  // EP-160: a setup-scope support session may not create tenant data.
  const setupScope = useSetupScopeSession()
  const { t } = useTranslation('units')
  const { t: tm } = useTranslation('meters')
  const { t: tc } = useTranslation('common')
  const { t: tp } = useTranslation('properties')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const unitId = Number(id)

  const { data: unit, isLoading, isFetching } = useUnit(unitId)
  const { data: roomType } = useRoomType(unit?.room_type_id || 0)
  const { data: property } = useProperty(roomType?.property_id || 0)
  const { data: meters, isLoading: isLoadingMeters } = useMeters(unitId)
  const { data: accountSettings } = useAccountSettings()
  const isCondominium = accountSettings?.account_type === 'condominium'
  // EP-134 §8 (T134-007) — Transfer Ownership is gated on the SAME
  // condominium_enabled setting the BE extends via CondominiumModuleGate
  // (the §8 widening §10 documents; per-property condominium flag alone
  // is too narrow — the FE-only enforcement of `settings.condominium_enabled`
  // is genuinely insufficient for an endpoint that ENDS the existing
  // shared-owner contract). Keeping the FE gate in sync with the BE
  // prevents the operator from seeing a button that immediately 403s.
  // The setting sits inside `accountSettings.settings.condominium_enabled`
  // (owner's per-account module toggle), not at the top of the payload —
  // the `AccountSettingsSerializer` deliberately keeps settings fields
  // namespaced under `settings` even though the top-level serializer
  // surfaces timezone/date_format/calendar at the top.
  const condominiumEnabled = !!accountSettings?.settings?.condominium_enabled

  // EP-134 §7 (T134-007) — derived selectors for the TransferOwnershipModal's
  // header + the §9 Q-2 tenant warning. The modal needs the OUTGOING owner
  // (any active OwnerContract — ownership-type Lease) and a flag for whether
  // the unit currently has an active TENANT RentalLease (the lease type is
  // rendered as `unit.current_lease` for exactly this reason: the unit's
  // `current_lease` only fires for the rental-subclass; the shared-owner
  // OwnerContract is intentionally excluded from `current_lease`).
  const activeOwnerContract = unit?.leases?.find(
    (l) => (l as { contract_type?: string }).contract_type === 'ownership'
      && (l as { ended_at?: string | null }).ended_at == null
  )
  const outgoingOwnerName =
    (activeOwnerContract as { customer?: { name?: string } } | undefined)?.customer?.name ?? null
  const hasActiveTenant = !!unit?.current_lease

  const deleteUnit = useDeleteUnit()
  const createMeter = useCreateMeter()
  const updateMeter = useUpdateMeter()
  const deleteMeter = useDeleteMeter()

  const [showMeterModal, setShowMeterModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editingMeter, setEditingMeter] = useState<Meter | null>(null)
  const [deletingMeter, setDeletingMeter] = useState<Meter | null>(null)
  // EP-134 §7 (T134-007) — Transfer ownership modal open state.
  const [showTransferModal, setShowTransferModal] = useState(false)
  // EP-136 T136-022/T136-023 — Book modal open state. This page only owns
  // the open/close plumbing; the form itself (customer search, dates,
  // amount, resolved-mode display, wired to useCreateBooking) lives in
  // BookingModal.
  const [showBookingModal, setShowBookingModal] = useState(false)
  const meterForm = useForm<MeterFormData>()
  const editMeterForm = useForm<EditMeterFormData>()

  const handleDeleteUnit = async () => {
    if (!unit || !roomType) return
    await deleteUnit.mutateAsync({ id: unitId, roomTypeId: unit.room_type_id })
    navigate(`/properties/${roomType.property_id}`)
  }

  const openMeterModal = () => {
    meterForm.reset({ meter_type: 'water', serial_number: '' })
    setShowMeterModal(true)
  }

  const onCreateMeter = async (data: MeterFormData) => {
    await createMeter.mutateAsync({
      unitId,
      meter_type: data.meter_type,
      serial_number: data.serial_number || undefined,
    })
    setShowMeterModal(false)
  }

  const openEditMeterModal = (meter: Meter) => {
    editMeterForm.reset({ serial_number: meter.serial_number ?? '' })
    setEditingMeter(meter)
  }

  const onEditMeter = async (data: EditMeterFormData) => {
    if (!editingMeter) return
    await updateMeter.mutateAsync({
      id: editingMeter.id,
      unitId,
      serial_number: data.serial_number || undefined,
    })
    setEditingMeter(null)
  }

  const onDeleteMeter = async () => {
    if (!deletingMeter) return
    await deleteMeter.mutateAsync({ id: deletingMeter.id, unitId })
    setDeletingMeter(null)
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-32" />
          </Card>
        </div>
    )
  }

  if (!unit) {
    return (
        <Card>
          <EmptyState
            title={t('empty.not_found')}
            action={<Button onClick={() => navigate('/properties')}>{tc('nav.properties')}</Button>}
          />
        </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name || tc('nav.properties'), href: `/properties/${roomType?.property_id}` },
            { label: `${t('title')} ${unit.name}` },
          ]}
        />

        <PageHeader
          title={`${t('title')} ${unit.name}`}
          description={`${roomType?.name || ''} - ${formatBaht(unit.price)}${tc('currency.per_month')}`}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — Record Payment is the primary CTA; hidden for
          // condominium (rent=0) so Edit promotes to solid instead (P005).
          primaryAction={{
            key: 'record-payment',
            label: tc('actions.record_payment'),
            icon: CreditCard,
            to: `/payments/record?unit_id=${unitId}`,
            hidden: isCondominium,
            testId: 'unit-record-payment',
          }}
          overflowActions={[
            // EP-136 T136-022 — Book. Gated on `unit.bookable === true`,
            // NEVER on `unit.status === 'vacant'`. A unit whose lease is
            // pending_signatures/pending_start reads status 'vacant' (only
            // an active lease counts as occupied) but the server's
            // UnitBookingService still refuses it with `unit_occupied` —
            // `bookable` is the server's own predicate for the exact same
            // check the booking transaction enforces, so this button and
            // the endpoint can't disagree. Also structurally excludes
            // condominium units (never bookable server-side).
            {
              key: 'book',
              label: t('actions.book'),
              icon: CalendarPlus,
              onClick: () => setShowBookingModal(true),
              hidden: unit.bookable !== true || setupScope,
              testId: 'unit-book',
            },
            // EP-134 §7 (T134-007) — Transfer ownership. Hidden on
            // non-condominium accounts (the BE re-checks via
            // CondominiumModuleGate; this is a UI parity guard, not a
            // security boundary). An apartment landlord never buys units
            // from themselves, so the action would only ever 422 / 403
            // there.
            {
              key: 'transfer-ownership',
              label: t('actions.transfer_ownership'),
              icon: User,
              onClick: () => setShowTransferModal(true),
              hidden: !condominiumEnabled || setupScope,
            },
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/units/${unitId}/edit`,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setConfirmDelete(true),
              destructive: true,
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Unit Details */}
          <Card className="lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">{t('unit_detail')}</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.unit_number')}</dt>
                <dd className="font-medium break-words">{unit.name}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.monthly_rent')}</dt>
                <dd className="font-medium">{formatBaht(unit.price)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.room_type')}</dt>
                <dd className="font-medium break-words">
                  <Link to={`/room-types/${unit.room_type_id}`} className="text-primary hover:underline">
                    {roomType?.name || '-'}
                  </Link>
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.property')}</dt>
                <dd className="font-medium break-words">
                  <Link to={`/properties/${roomType?.property_id}`} className="text-primary hover:underline">
                    {property?.name || '-'}
                  </Link>
                </dd>
              </div>
            </dl>
          </Card>

          {/* Status */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.status')}</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">{t('labels.occupancy')}</span>
                {/* EP-136 T136-020 — three-way status badge: vacant=success,
                    reserved=warning (held by a booking fee, not yet a tenant),
                    occupied=ghost (unchanged). */}
                <Badge
                  variant={unit.status === 'vacant' ? 'success' : unit.status === 'reserved' ? 'warning' : 'ghost'}
                >
                  {unit.status === 'vacant'
                    ? t('status.vacant')
                    : unit.status === 'reserved'
                      ? t('status.reserved')
                      : t('status.occupied')}
                </Badge>
              </div>
              {unit.current_lease && (
                <div className="pt-3 border-t border-base-200">
                  <div className="flex items-center gap-2 text-sm text-base-content/60 mb-1">
                    <User className="w-3 h-3" />
                    {t('labels.current_tenant')}
                  </div>
                  <Link
                    to={`/leases/${unit.current_lease.id}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {unit.current_lease.customer?.name || t('actions.view_lease')}
                  </Link>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* EP-136 T136-022 — reservation card. Mutually exclusive with the
            Book action above: a reserved unit is never bookable, and a
            bookable/occupied unit never carries a current_booking. The card
            reads its cancel-variant + forfeit-default straight off
            unit.current_booking's frozen snapshot (EP-136 follow-up) — no
            property/account settings props needed any more. */}
        {unit.status === 'reserved' && unit.current_booking && (
          <UnitBookingCard booking={unit.current_booking} />
        )}

        {/* EP-134 §2b (T134-007) — ownership-history block. Only rendered when
            the unit has any OwnerContract rows (active or ended); the
            component returns null otherwise, which keeps the apartment
            account's unit page identical to pre-EP-134. */}
        <OwnershipHistory unitId={unitId} />

        {/* Pricing Formula */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">{t('labels.utility_rates_override')}</h2>
          {unit.pricing_formula && unit.pricing_formula.length > 0 ? (
            <>
              <p className="text-sm text-base-content/60 mb-3">{t('labels.custom_rates')}</p>
              <div className="space-y-2">
                {unit.pricing_formula.map((item, index) => (
                  <div
                    key={item.key || index}
                    className="flex justify-between items-center p-3 bg-base-200 rounded-lg"
                  >
                    <span>{item.name ?? (item as { label?: string }).label ?? item.key}</span>
                    <span className="font-medium">
                      {item.type === 'fixed'
                        ? `${formatBaht(item.amount ?? 0)}${tc('currency.per_month')}`
                        : item.type === 'percentage'
                          ? `${item.rate ?? 0}%`
                          : item.type === 'discount'
                            ? `-${formatBaht(item.amount ?? 0)}`
                            : item.type === 'per_sqm'
                              ? `${formatBaht(item.rate ?? 0)} ${tp('pricing_editor.per_sqm_unit')}`
                              : item.type === 'per_share_ratio'
                                ? `${formatBaht(item.rate ?? 0)} ${tp('pricing_editor.per_share_ratio_unit')}`
                                : `${formatBaht(item.rate ?? 0)}${item.unit ? `/${item.unit}` : ''}${item.minimum ? ` ${tp('pricing_editor.minimum_suffix', { amount: formatBaht(item.minimum) })}` : ''}`}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-base-content/60">{t('labels.uses_property_default')}</p>
          )}
        </Card>

        {/* Meters */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{tm('title')}</h2>
            <div className="flex items-center gap-2">
              {roomType?.property_id && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Gauge className="w-4 h-4" />}
                  onClick={() => navigate(`/properties/${roomType.property_id}/readings`)}
                >
                  {tm('bulk_readings')}
                </Button>
              )}
              <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={openMeterModal}>
                {tm('actions.add_meter')}
              </Button>
            </div>
          </div>

          {isLoadingMeters ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : !meters || meters.length === 0 ? (
            <SetupGuide
              icon={<Droplets className="w-16 h-16" />}
              title={tm('setup_guide.title')}
              description={tm('setup_guide.description')}
              steps={[
                { title: tm('setup_guide.step_1_title'), description: tm('setup_guide.step_1_description') },
                { title: tm('setup_guide.step_2_title'), description: tm('setup_guide.step_2_description') },
                { title: tm('setup_guide.step_3_title'), description: tm('setup_guide.step_3_description') },
              ]}
              action={
                <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={openMeterModal}>
                  {tm('actions.add_meter')}
                </Button>
              }
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {meters.map((meter) => (
                <div
                  key={meter.id}
                  className="p-4 border border-base-200 rounded-lg transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <Link
                      to={`/meters/${meter.id}`}
                      className="flex items-center gap-3 hover:text-primary transition-colors flex-1 min-w-0"
                    >
                      {meter.meter_type === 'water' ? (
                        <Droplets className="w-5 h-5 text-info shrink-0" />
                      ) : (
                        <Zap className="w-5 h-5 text-warning shrink-0" />
                      )}
                      <span className="font-medium truncate">
                        {meter.meter_type === 'water' ? tm('types.water') : tm('types.electricity')}
                      </span>
                    </Link>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {meter.referenced_by_invoices ? (
                        <Badge variant="ghost" className="text-xs">
                          {tm('labels.used_in_invoices')}
                        </Badge>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="xs"
                            shape="circle"
                            aria-label={tm('actions.edit_meter')}
                            onClick={() => openEditMeterModal(meter)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            shape="circle"
                            className="text-error"
                            aria-label={tm('actions.delete_meter')}
                            onClick={() => setDeletingMeter(meter)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <Link to={`/meters/${meter.id}`} className="block hover:text-primary transition-colors">
                    <div className="text-sm text-base-content/60">
                      {meter.serial_number || tm('empty.no_serial_number')}
                    </div>
                    {meter.latest_reading && (
                      <div className="text-sm mt-2">
                        {tm('labels.latest')}: <span className="font-medium">{meter.latest_reading.current_value}</span>
                      </div>
                    )}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDeleteUnit}
        title={tc('actions.delete')}
        message={t('messages.delete_unit_cannot_undo', { name: unit?.name })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteUnit.isPending}
      />

      {/* Add Meter Modal */}
      <Modal
        isOpen={showMeterModal}
        onClose={() => setShowMeterModal(false)}
        title={tm('actions.add_meter')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowMeterModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={meterForm.handleSubmit(onCreateMeter)} loading={createMeter.isPending}>
              {tm('actions.add_meter')}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <Select
            label={tm('labels.meter_type')}
            options={[
              { value: 'water', label: tm('types.water') },
              { value: 'electricity', label: tm('types.electricity') },
            ]}
            {...meterForm.register('meter_type', { required: tc('errors.required') })}
          />
          <Input
            label={tm('labels.serial_number_optional')}
            placeholder={tm('placeholders.serial_number')}
            {...meterForm.register('serial_number')}
          />
        </form>
      </Modal>

      {/* Edit Meter Modal */}
      <Modal
        isOpen={!!editingMeter}
        onClose={() => setEditingMeter(null)}
        title={tm('actions.edit_meter')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditingMeter(null)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={editMeterForm.handleSubmit(onEditMeter)} loading={updateMeter.isPending}>
              {tc('actions.save')}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* meter_type is read-only — useUpdateMeter only accepts serial_number */}
          <div>
            <span className="label-text text-sm font-medium">{tm('labels.meter_type')}</span>
            <div className="mt-1 text-base-content/80">
              {editingMeter?.meter_type === 'water' ? tm('types.water') : tm('types.electricity')}
            </div>
          </div>
          <Input
            label={tm('labels.serial_number_optional')}
            placeholder={tm('placeholders.serial_number')}
            {...editMeterForm.register('serial_number')}
          />
        </form>
      </Modal>

      {/* Delete Meter ConfirmDialog */}
      <ConfirmDialog
        isOpen={!!deletingMeter}
        onClose={() => setDeletingMeter(null)}
        onConfirm={onDeleteMeter}
        title={tm('actions.delete_meter')}
        message={tm('messages.delete_meter_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteMeter.isPending}
      />

      {/* EP-134 §7 (T134-007) — Transfer ownership modal. Mounted only on
          condominium-enabled accounts (the overflow button is hidden
          otherwise; this is a defence-in-depth check — the BE would 403
          before this modal ever appeared). The outgoing-owner display name
          is passed in for the modal header + warning copy; the §9 Q-2
          tenant hint is wired to `unit.current_lease` (rental-only path). */}
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        unitId={unitId}
        outgoingOwnerName={outgoingOwnerName}
        hasActiveTenant={hasActiveTenant}
      />

      {/* EP-136 T136-023 — Book modal. property may still be loading on a
          very fast click; BookingModal handles that gracefully (falls back
          to the account-level booking-settings default only). */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        unitId={unitId}
        property={property}
      />
    </>
  )
}
